<template>
  <div>
    <v-btn
      color="primary"
      small
      elevation="0"
      @click="handleOpen"
      class="text-capitalize"
    >
      <v-icon small left>mdi-calendar</v-icon>
      {{ buttonLabel }}
    </v-btn>

    <v-dialog
      v-model="dialog"
      max-width="400"
      persistent
    >
      <v-card>
        <v-card-title class="text-subtitle-1 pb-0">
          Pilih Rentang Tanggal
        </v-card-title>

        <v-card-text class="pt-0" style="margin-top: -12px !important;">
          <v-radio-group
            v-model="tempLabel"
            @change="handleRadioChange"
            dense
          >
            <v-radio
              v-for="preset in presets"
              :key="preset.label"
              :label="preset.label"
              :value="preset.label"
              class="my-2"
            >
              <template v-slot:label>
                <span class="text-body-2 black--text">{{ preset.label }}</span>
              </template>
            </v-radio>
          </v-radio-group>

          <!-- Custom Date Range Inputs -->
          <v-expand-transition>
            <div v-if="tempLabel === 'Rentang Tanggal'" class="mt-3">
              <v-row dense>
                <v-col cols="6">
                  <v-menu
                    v-model="startDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="tempStartDateFormatted"
                        label="Tanggal Mulai"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        dense
                        outlined
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                        class="text-caption"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="tempStartDatePicker"
                      @input="handleStartDateChange"
                      :max="tempEndDatePicker || undefined"
                      locale="id"
                      no-title
                    ></v-date-picker>
                  </v-menu>
                </v-col>

                <v-col cols="6">
                  <v-menu
                    v-model="endDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="tempEndDateFormatted"
                        label="Tanggal Akhir"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        dense
                        outlined
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                        class="text-caption"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="tempEndDatePicker"
                      @input="endDateMenu = false"
                      :min="tempStartDatePicker || undefined"
                      locale="id"
                      no-title
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>

              <div class="text-caption grey--text mt-2">
                Pilih tanggal mulai dan akhir untuk rentang yang diinginkan. 
                Pastikan tanggal akhir tidak lebih awal dari tanggal mulai.
              </div>
            </div>
          </v-expand-transition>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            color="error"
            small
            @click="handleCancel"
            class="text-capitalize"
          >
            Batal
          </v-btn>
          <v-btn
            color="primary"
            small
            elevation="0"
            @click="handleApply"
            :disabled="isApplyDisabled"
            class="text-capitalize"
          >
            Terapkan
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment'
import 'moment/locale/id'

moment.locale('id')

export default {
  name: 'DateFilter',
  props: {
    value: {
      type: Object,
      default: () => ({ start: null, end: null })
    }
  },
  data() {
    const today = moment().startOf('day')
    
    return {
      dialog: false,
      selectedLabel: 'Hari ini',
      startDate: today.clone(),
      endDate: today.clone(),
      
      // Temporary state untuk dialog
      tempLabel: 'Hari ini',
      tempStartDate: today.clone(),
      tempEndDate: today.clone(),
      tempStartDatePicker: today.format('YYYY-MM-DD'),
      tempEndDatePicker: today.format('YYYY-MM-DD'),
      
      // Menu states
      startDateMenu: false,
      endDateMenu: false
    }
  },
  computed: {
    presets() {
      const today = moment().startOf('day')
      const yesterday = moment().subtract(1, 'day').startOf('day')
      const startOfMonth = moment().startOf('month')
      const endOfMonth = moment().endOf('month')
      const startOfLastMonth = moment().subtract(1, 'month').startOf('month')
      const endOfLastMonth = moment().subtract(1, 'month').endOf('month')
      const startOfYear = moment().startOf('year')
      const startOfLastYear = moment().subtract(1, 'year').startOf('year')
      const endOfLastYear = moment().subtract(1, 'year').endOf('year')

      return [
        { label: 'Semua Tanggal', start: null, end: null },
        { label: 'Hari ini', start: today, end: today },
        { label: 'Kemarin', start: yesterday, end: yesterday },
        { label: '7 Hari Terakhir', start: moment().subtract(6, 'day').startOf('day'), end: today },
        { label: 'Bulan Ini', start: startOfMonth, end: today },
        { label: '3 Bulan Terakhir', start: moment().subtract(3, 'month').startOf('month'), end: today },
        { label: 'Bulan Kemarin', start: startOfLastMonth, end: endOfLastMonth },
        { label: 'Tahun Ini', start: startOfYear, end: today },
        { label: 'Tahun Kemarin', start: startOfLastYear, end: endOfLastYear },
        { label: 'Rentang Tanggal', start: today, end: today }
      ]
    },
    buttonLabel() {
      if (this.selectedLabel === 'Semua Tanggal') {
        return 'Semua Tanggal'
      } else if (this.startDate && this.endDate) {
        return `${this.startDate.format('DD MMMM YYYY')} - ${this.endDate.format('DD MMMM YYYY')}`
      }
      return 'Pilih Tanggal'
    },
    tempStartDateFormatted() {
      return this.tempStartDate ? this.tempStartDate.format('DD MMM YYYY') : ''
    },
    tempEndDateFormatted() {
      return this.tempEndDate ? this.tempEndDate.format('DD MMM YYYY') : ''
    },
    isApplyDisabled() {
      if (this.tempLabel === 'Semua Tanggal') {
        return false
      }
      return !this.tempStartDate || !this.tempEndDate
    }
  },
  created() {
    // Emit initial value saat component dibuat
    this.$emit('change', { 
      start: this.startDate.clone(), 
      end: this.endDate.clone() 
    })
    this.$emit('input', { 
      start: this.startDate.clone(), 
      end: this.endDate.clone() 
    })
  },
  methods: {
    handleRadioChange() {
      if (this.tempLabel === 'Semua Tanggal') {
        this.tempStartDate = null
        this.tempEndDate = null
        this.tempStartDatePicker = null
        this.tempEndDatePicker = null
      } else if (this.tempLabel !== 'Rentang Tanggal') {
        const preset = this.presets.find(p => p.label === this.tempLabel)
        if (preset) {
          this.tempStartDate = preset.start
          this.tempEndDate = preset.end
          this.tempStartDatePicker = preset.start ? preset.start.format('YYYY-MM-DD') : null
          this.tempEndDatePicker = preset.end ? preset.end.format('YYYY-MM-DD') : null
        }
      }
    },
    handleStartDateChange(val) {
      this.startDateMenu = false
      this.tempStartDate = moment(val)
      this.tempEndDate = null
      this.tempEndDatePicker = null
    },
    handleApply() {
      if (this.tempLabel === 'Semua Tanggal') {
        this.selectedLabel = this.tempLabel
        this.startDate = null
        this.endDate = null
        this.$emit('change', { start: null, end: null })
        this.$emit('input', { start: null, end: null })
        this.dialog = false
      } else if (this.tempStartDate && this.tempEndDate) {
        this.selectedLabel = this.tempLabel
        this.startDate = this.tempStartDate.clone()
        this.endDate = this.tempEndDate.clone()
        this.$emit('change', { 
          start: this.tempStartDate.clone(), 
          end: this.tempEndDate.clone() 
        })
        this.$emit('input', { 
          start: this.tempStartDate.clone(), 
          end: this.tempEndDate.clone() 
        })
        this.dialog = false
      }
    },
    handleOpen() {
      // Backup current state
      this.tempLabel = this.selectedLabel
      this.tempStartDate = this.startDate ? this.startDate.clone() : null
      this.tempEndDate = this.endDate ? this.endDate.clone() : null
      this.tempStartDatePicker = this.startDate ? this.startDate.format('YYYY-MM-DD') : null
      this.tempEndDatePicker = this.endDate ? this.endDate.format('YYYY-MM-DD') : null
      this.dialog = true
    },
    handleCancel() {
      this.dialog = false
    }
  },
  watch: {
    tempStartDatePicker(val) {
      if (val) {
        this.tempStartDate = moment(val)
      }
    },
    tempEndDatePicker(val) {
      if (val) {
        this.tempEndDate = moment(val)
      }
    }
  }
}
</script>

<style scoped>
.v-radio {
  margin-bottom: 0 !important;
}

.v-input--radio-group >>> .v-input__control {
  width: 100%;
}

.v-text-field >>> .v-input__prepend-inner {
  margin-top: 8px !important;
}

.v-text-field >>> input {
  font-size: 12px !important;
}

.v-text-field >>> .v-label {
  font-size: 12px !important;
}
</style>

